/**
 * Recovery context type definitions
 */

// Recovery flow steps
export const RecoveryStep = {
  IDLE: 'idle',
  AWAIT_KEY: 'await_key',
  VALIDATING: 'validating',
  RESTORING: 'restoring',
  SSSS: 'ssss',
  VERIFYING: 'verifying',
  DONE: 'done',
  ERROR: 'error',
};

/**
 * Recovery error structure
 * @typedef {Object} RecoveryError
 * @property {string} code - Error code for programmatic handling
 * @property {string} message - Human-readable error message
 */

/**
 * Recovery progress information
 * @typedef {Object} RecoveryProgress
 * @property {number} imported - Number of items imported
 * @property {number} total - Total items to import
 * @property {number} percent - Computed percentage (0-100)
 */

/**
 * Main recovery state structure
 * @typedef {Object} RecoveryState
 * @property {boolean} hasBackup - Whether backup exists on server
 * @property {boolean} needsRecovery - Whether recovery is needed
 * @property {string} step - Current step in recovery flow
 * @property {RecoveryProgress} progress - Import progress
 * @property {RecoveryError} [error] - Current error if any
 * @property {number} [startedAt] - Timestamp when recovery started
 */

// Action types
export const ActionType = {
  INIT: 'INIT',
  OPEN_MODAL: 'OPEN_MODAL',
  SUBMIT_KEY: 'SUBMIT_KEY',
  RESTORE: 'RESTORE',
  SSSS: 'SSSS',
  VERIFY_DEVICE: 'VERIFY_DEVICE',
  COMPLETE: 'COMPLETE',
  FAIL: 'FAIL',
  RESET: 'RESET',
  SKIP: 'SKIP',
  SET_PROGRESS: 'SET_PROGRESS',
  SET_STEP: 'SET_STEP',
  SET_STARTED_AT: 'SET_STARTED_AT',
  CLEAR_ERROR: 'CLEAR_ERROR',
};

/**
 * Action definitions with payloads
 */
export const createAction = {
  init: (payload) => ({ type: ActionType.INIT, payload }),
  // Optionally accept a timestamp to set startedAt deterministically
  openModal: (timestamp) => ({ type: ActionType.OPEN_MODAL, payload: { timestamp } }),
  submitKey: (key) => ({ type: ActionType.SUBMIT_KEY, payload: { key } }),
  restore: () => ({ type: ActionType.RESTORE }),
  ssss: () => ({ type: ActionType.SSSS }),
  verifyDevice: () => ({ type: ActionType.VERIFY_DEVICE }),
  complete: () => ({ type: ActionType.COMPLETE }),
  fail: (error) => ({ type: ActionType.FAIL, payload: error }),
  reset: () => ({ type: ActionType.RESET }),
  skip: () => ({ type: ActionType.SKIP }),
  setProgress: (progress) => ({ type: ActionType.SET_PROGRESS, payload: progress }),
  // Accept either (step) or (step, timestamp)
  setStep: (step, timestamp) => ({ type: ActionType.SET_STEP, payload: { step, timestamp } }),
  setStartedAt: (timestamp) => ({ type: ActionType.SET_STARTED_AT, payload: timestamp }),
  clearError: () => ({ type: ActionType.CLEAR_ERROR }),
};

/**
 * Initial state for recovery context
 */
export const initialState = {
  hasBackup: false,
  needsRecovery: false,
  step: RecoveryStep.IDLE,
  progress: {
    imported: 0,
    total: 0,
    percent: 0,
  },
  error: null,
  startedAt: null,
};

/**
 * Valid state transitions map
 * Defines which steps can transition to which other steps
 */
export const validTransitions = {
  [RecoveryStep.IDLE]: [RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.AWAIT_KEY]: [RecoveryStep.VALIDATING, RecoveryStep.IDLE, RecoveryStep.ERROR],
  [RecoveryStep.VALIDATING]: [RecoveryStep.RESTORING, RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.RESTORING]: [RecoveryStep.SSSS, RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.SSSS]: [RecoveryStep.VERIFYING, RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.VERIFYING]: [RecoveryStep.DONE, RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.DONE]: [RecoveryStep.IDLE, RecoveryStep.AWAIT_KEY, RecoveryStep.ERROR],
  [RecoveryStep.ERROR]: [RecoveryStep.IDLE, RecoveryStep.AWAIT_KEY],
};

/**
 * Check if a transition is valid
 * @param {string} fromStep - Current step
 * @param {string} toStep - Target step
 * @returns {boolean}
 */
export function isValidTransition(fromStep, toStep) {
  const allowedSteps = validTransitions[fromStep] || [];
  return allowedSteps.includes(toStep);
}

/**
 * Compute percentage from imported/total
 * @param {number} imported - Items imported
 * @param {number} total - Total items
 * @returns {number} Percentage (0-100)
 */
export function computePercent(imported, total) {
  if (!total || total === 0) return 0;
  const percent = Math.round((imported / total) * 100);
  return Math.min(100, Math.max(0, percent)); // Clamp between 0 and 100
}
